import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Typography,
  TextField,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { toDateKey } from "../../utils/dateTimeUtils";

const API_BASE = process.env.REACT_APP_API_BASE || "";
const DAY_CELL_SIZE = 92;
const GRID_GAP_PX = 8;
const CALENDAR_GRID_WIDTH = DAY_CELL_SIZE * 7 + GRID_GAP_PX * 6;
const NOTES_WIDTH = 360;
const monthNames = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

const getManilaDate = () => {
  try {
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Manila",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const [y, m, d] = fmt.format(new Date()).split("-").map(Number);
    return new Date(y, m - 1, d);
  } catch (e) {
    return new Date();
  }
};

const buildCalendar = (monthIndex, year) => {
  const firstDay = new Date(year, monthIndex, 1).getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const weeks = [];
  let day = 1 - firstDay;
  for (let w = 0; w < 6; w += 1) {
    const week = [];
    for (let d = 0; d < 7; d += 1) {
      week.push(day > 0 && day <= daysInMonth ? day : null);
      day += 1;
    }
    weeks.push(week);
  }
  return weeks;
};

export default function PersonalCalendar({
  employeeId,
  holidays,
  initialMonth,
  today,
  onMonthChange,
  onDayClick,
  enableNotes = true,
}) {
  const manilaToday = useMemo(() => today || getManilaDate(), [today]);
  const defaultMonth = useMemo(() => {
    if (initialMonth instanceof Date) {
      return { year: initialMonth.getFullYear(), monthIndex: initialMonth.getMonth() };
    }
    if (initialMonth && Number.isFinite(initialMonth.year) && Number.isFinite(initialMonth.monthIndex)) {
      return { year: initialMonth.year, monthIndex: initialMonth.monthIndex };
    }
    const d = getManilaDate();
    if (d.getDate() === 1) {
      return { year: d.getMonth() === 0 ? d.getFullYear() - 1 : d.getFullYear(), monthIndex: d.getMonth() === 0 ? 11 : d.getMonth() - 1 };
    }
    return { year: d.getFullYear(), monthIndex: d.getMonth() };
  }, [initialMonth]);

  const [monthIndex, setMonthIndex] = useState(defaultMonth.monthIndex);
  const [year, setYear] = useState(defaultMonth.year);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerAnchorEl, setPickerAnchorEl] = useState(null);
  const [pickerMonth, setPickerMonth] = useState(defaultMonth.monthIndex);
  const [pickerYear, setPickerYear] = useState(defaultMonth.year);
  const [internalHolidays, setInternalHolidays] = useState([]);

  const [selectedDate, setSelectedDate] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [notesByDate, setNotesByDate] = useState({});

  const storageKey = useMemo(
    () => (employeeId ? `calendarNotes_${employeeId}` : "calendarNotes_anonymous"),
    [employeeId]
  );

  useEffect(() => {
    if (!enableNotes) return;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          setNotesByDate(parsed);
        }
      }
    } catch (e) {
      console.warn("Failed to load calendar notes", e);
    }
  }, [storageKey, enableNotes]);

  const saveNotes = (next) => {
    setNotesByDate(next);
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(next));
    } catch (e) {
      console.warn("Failed to save calendar notes", e);
    }
  };

  const handleSelectDate = (dateKey) => {
    if (!enableNotes) return;
    setSelectedDate(dateKey);
    setNoteText(notesByDate[dateKey] || "");
  };

  const handleSaveNote = () => {
    if (!enableNotes || !selectedDate) return;
    const next = { ...notesByDate };
    if (noteText.trim()) next[selectedDate] = noteText.trim();
    else delete next[selectedDate];
    saveNotes(next);
  };

  useEffect(() => {
    if (typeof onMonthChange === "function") {
      onMonthChange(year, monthIndex);
    }
  }, [monthIndex, year, onMonthChange]);

  useEffect(() => {
    if (holidays) return;
    (async () => {
      try {
        const q = `?month=${monthIndex + 1}&year=${year}`;
        const res = await fetch(`${API_BASE}/api/holidays${q}`, { credentials: "include" });
        const data = await res.json();
        const rows = Array.isArray(data) ? data : (Array.isArray(data?.holidays) ? data.holidays : []);
        setInternalHolidays(rows || []);
      } catch (e) {
        console.warn("Failed to load holidays", e);
        setInternalHolidays([]);
      }
    })();
  }, [monthIndex, year, holidays]);

  const holidayMap = useMemo(() => {
    const map = {};
    const list = Array.isArray(holidays) ? holidays : internalHolidays;
    for (const h of list || []) {
      const dateRaw = h.date || h.holiday_date || h.holidayDate;
      const dateStr = toDateKey(dateRaw);
      if (!dateStr) continue;
      const typeRaw = h.holiday_type || h.holidayType || h.type || h.kind || h.category || "";
      const type = String(typeRaw).toLowerCase() === "special" ? "special" : "regular";
      map[dateStr] = {
        id: h.id || h.holiday_id || h.holidayId || null,
        description: h.description || h.reason || h.name || "Holiday",
        type,
      };
    }
    return map;
  }, [holidays, internalHolidays]);

  const calendarWeeks = useMemo(() => buildCalendar(monthIndex, year), [monthIndex, year]);
  const yearOptions = useMemo(() => {
    const list = [];
    for (let y = year - 5; y <= year + 5; y += 1) list.push(y);
    return list;
  }, [year]);

  const handlePrevMonth = () => {
    let m = monthIndex - 1;
    let y = year;
    if (m < 0) { m = 11; y -= 1; }
    setMonthIndex(m);
    setYear(y);
  };

  const handleNextMonth = () => {
    let m = monthIndex + 1;
    let y = year;
    if (m > 11) { m = 0; y += 1; }
    setMonthIndex(m);
    setYear(y);
  };

  const handleOpenPicker = (e) => {
    setPickerAnchorEl(e.currentTarget);
    setPickerMonth(monthIndex);
    setPickerYear(year);
    setPickerOpen(true);
  };

  const handleClosePicker = () => {
    setPickerOpen(false);
    setPickerAnchorEl(null);
  };

  const handleApplyPicker = () => {
    setMonthIndex(pickerMonth);
    setYear(pickerYear);
    handleClosePicker();
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        alignItems: "flex-start",
        width: "100%",
        flexWrap: { xs: "wrap", md: "nowrap" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          flex: { xs: "1 1 100%", md: `0 0 ${CALENDAR_GRID_WIDTH}px` },
          width: { xs: "100%", md: CALENDAR_GRID_WIDTH },
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: { xs: 0.5, sm: 1 },
            mb: 1,
          }}
        >
          <IconButton size="small" onClick={handlePrevMonth} aria-label="Previous month">
            <ChevronLeft fontSize="small" />
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              cursor: "pointer",
              userSelect: "none",
              textAlign: "center",
              flex: 1,
              minWidth: 0,
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            onClick={handleOpenPicker}
          >
            {monthNames[monthIndex]} {year}
          </Typography>
          <IconButton size="small" onClick={handleNextMonth} aria-label="Next month">
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>

        <Popover
          open={pickerOpen}
          anchorEl={pickerAnchorEl}
          onClose={handleClosePicker}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Box sx={{ p: { xs: 1.25, sm: 2 }, display: "grid", gap: { xs: 1, sm: 1.5 }, minWidth: { xs: 180, sm: 220 } }}>
            <Select size="small" value={pickerMonth} onChange={(e) => setPickerMonth(Number(e.target.value))}>
              {monthNames.map((m, idx) => (
                <MenuItem key={m} value={idx}>{m}</MenuItem>
              ))}
            </Select>
            <Select size="small" value={pickerYear} onChange={(e) => setPickerYear(Number(e.target.value))}>
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
            <Button variant="contained" size="small" onClick={handleApplyPicker}>Go</Button>
          </Box>
        </Popover>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(7, minmax(0, 1fr))", md: `repeat(7, ${DAY_CELL_SIZE}px)` },
            gap: 1,
            mb: 0.5,
            width: "100%",
          }}
        >
          {["SUN","MON","TUE","WED","THU","FRI","SAT"].map((d) => (
            <Box
              key={d}
              sx={{
                textAlign: "center",
                fontSize: { xs: 10, sm: 12 },
                color: "text.secondary",
              }}
            >
              {d}
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(7, minmax(0, 1fr))", md: `repeat(7, ${DAY_CELL_SIZE}px)` },
            gap: 1,
            width: "100%",
          }}
        >
          {calendarWeeks.map((week, wi) =>
            week.map((day, di) => {
              const isWeekend = di === 0 || di === 6;
              const isToday = (year === manilaToday.getFullYear() && monthIndex === manilaToday.getMonth() && day === manilaToday.getDate());
              const dateStr = day ? `${year}-${String(monthIndex + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}` : null;
              const holidayObj = dateStr ? holidayMap[dateStr] : null;
              const holidayType = holidayObj && holidayObj.type === "special" ? "special" : "regular";
              const holidayColor = holidayType === "special" ? "#ff9800" : "#d32f2f";
              const holidayBg = holidayType === "special" ? "#fff3e0" : "#ffebee";
              const holidayLabel = holidayObj ? `Holiday: ${holidayObj.description || "Holiday"}` : "";
              const label = holidayObj ? holidayLabel : (isWeekend ? "No work day" : "");
              const bg = isToday ? "#2f74d0" : (holidayObj ? holidayBg : (isWeekend ? "#f8d7da" : "transparent"));
              return (
                <Box
                  key={`w${wi}d${di}`}
                  sx={{
                    height: { xs: "auto", md: DAY_CELL_SIZE },
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  {day ? (
                    <Box
                      onClick={() => {
                        if (onDayClick) onDayClick(dateStr, holidayObj);
                        handleSelectDate(dateStr);
                      }}
                      sx={{
                        p: { xs: 0.5, sm: 1 },
                        borderRadius: 1,
                        cursor: onDayClick ? "pointer" : "default",
                        backgroundColor: bg,
                        color: isToday ? "white" : undefined,
                        height: { xs: "100%", md: DAY_CELL_SIZE },
                        width: "100%",
                        minHeight: { xs: 52, sm: 68, md: DAY_CELL_SIZE },
                        aspectRatio: { xs: "1 / 1", md: "auto" },
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                        overflow: "hidden",
                        border: selectedDate === dateStr ? "2px solid #1976d2" : "1px solid #e0e0e0",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: isToday ? 700 : 500, fontSize: { xs: "0.78rem", sm: "0.875rem" } }}
                      >
                        {day}
                      </Typography>
                      {label && (
                        <Typography
                          variant="caption"
                          sx={{
                            mt: 0.5,
                            fontSize: { xs: "0.55rem", sm: "0.70rem" },
                            wordBreak: "break-word",
                            display: "-webkit-box",
                            WebkitLineClamp: { xs: 1, sm: 2 },
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            color: holidayObj ? holidayColor : (isToday ? "white" : "text.secondary"),
                          }}
                        >
                          {label}
                        </Typography>
                      )}
                      {enableNotes && notesByDate[dateStr] && (
                        <Typography variant="caption" sx={{ mt: "auto", fontStyle: "italic", color: "text.secondary" }}>
                          Note saved
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        height: { xs: "100%", md: DAY_CELL_SIZE },
                        width: "100%",
                        minHeight: { xs: 52, sm: 68, md: DAY_CELL_SIZE },
                        aspectRatio: { xs: "1 / 1", md: "auto" },
                      }}
                    />
                  )}
                </Box>
              );
            })
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mt: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Box sx={{ width: 10, height: 10, bgcolor: "#d32f2f", borderRadius: "50%" }} /> <Typography variant="caption">Regular Holiday</Typography></Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Box sx={{ width: 10, height: 10, bgcolor: "#ff9800", borderRadius: "50%" }} /> <Typography variant="caption">Special Holiday</Typography></Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Box sx={{ width: 10, height: 10, bgcolor: "#f8d7da", borderRadius: 1 }} /> <Typography variant="caption">Weekend (No work day)</Typography></Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Box sx={{ width: 10, height: 10, bgcolor: "#2f74d0", borderRadius: 1 }} /> <Typography variant="caption">Today</Typography></Box>
        </Box>
      </Box>

      {enableNotes && (
        <Box
          sx={{
            width: { xs: "100%", md: NOTES_WIDTH },
            flex: { xs: "1 1 100%", md: `0 0 ${NOTES_WIDTH}px` },
            alignSelf: "start",
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            {selectedDate ? `Notes for ${selectedDate}` : "Notes"}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={14}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Write what to do on this day (e.g., follow-up, OT, reminder)..."
            sx={{ width: "100%" }}
          />
          <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="contained" size="small" onClick={handleSaveNote} disabled={!selectedDate}>
              Save note
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
